import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import { Dispatch, SetStateAction } from 'react';
import { EntityType, FenceZone } from '../../../util/enums';
import { jsUcFirst } from '../../Global/StringFormatterFunctions';
import { GeomobyOverride } from '../types';
import { Property } from '../../ProjectConfig/types';
import { PropertyType } from '../../ProjectConfig/enums';
import { FRESH, UPDATED } from '../../../util/constants';
import { DialogType } from './enums';
import { polygonsContainedWithinPolygon } from './Draw';
import { findBufferZone } from '../Helpers';
import { Map as OlMap } from 'ol';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';

export const foundFeatureWithUndefinedName = (
  features: Feature<Geometry>[],
  type: EntityType.Geofence | EntityType.Microfence,
  setOpenDialog: Dispatch<
    SetStateAction<
      | {
          type: DialogType;
          title: string;
        }
      | undefined
    >
  >,
): Feature<Geometry> | undefined => {
  const hasUndefinedName = features.find(f => !f.get('name'));
  if (hasUndefinedName !== undefined) {
    setOpenDialog({
      type: DialogType.DirtySave,
      title: hasUndefinedName ? `${jsUcFirst(type)} name is required` : '',
    });
  }
  return hasUndefinedName;
};

export const foundFeatureWithDuplicateName = (
  features: Feature<Geometry>[],
  type: EntityType.Geofence | EntityType.Microfence,
  setOpenDialog: Dispatch<
    SetStateAction<
      | {
          type: DialogType;
          title: string;
        }
      | undefined
    >
  >,
): Feature<Geometry> | undefined => {
  const hasDuplicateName = features.find(feature =>
    features.find(
      f =>
        feature.get('name') === f.get('name') &&
        feature.get('id') !== f.get('id') &&
        (f.get(FRESH) || f.get(UPDATED)),
    ),
  );
  if (hasDuplicateName !== undefined) {
    setOpenDialog({
      type: DialogType.DirtySave,
      title: hasDuplicateName ? `${jsUcFirst(type)} name must be unique.` : '',
    });
  }
  return hasDuplicateName;
};

export const foundFeatureWithEmptyGeomobyPropertyValue = (
  features: Feature<Geometry>[],
  setOpenDialog: Dispatch<
    SetStateAction<
      | {
          type: DialogType;
          title: string;
        }
      | undefined
    >
  >,
): Feature<Geometry> | undefined => {
  const hasEmptyGeomobyPropertyName = features.find(
    f =>
      JSON.stringify(Object.entries(f.get('geomobyProperties')))?.includes('["",') ||
      JSON.stringify(Object.entries(f.get('geomobyProperties')))?.includes(',""'),
  );

  if (hasEmptyGeomobyPropertyName !== undefined) {
    setOpenDialog({
      type: DialogType.DirtySave,
      title: hasEmptyGeomobyPropertyName ? 'Geofence Property values must not be left empty' : '',
    });
  }
  return hasEmptyGeomobyPropertyName;
};

export const foundFeatureWithIncompleteOverride = (
  features: Feature<Geometry>[],
  setOpenDialog: Dispatch<
    SetStateAction<
      | {
          type: DialogType;
          title: string;
        }
      | undefined
    >
  >,
): Feature<Geometry> | undefined => {
  return features.find(f =>
    f.get('geomobyOverrides')?.find((override: GeomobyOverride) => {
      if (!override.property || !override.value || override.deviceIds?.length === 0) {
        setOpenDialog({
          type: DialogType.DirtySave,
          title: `All Geomoby Overrides ${
            override.deviceIds?.length === 0 ? 'require device IDs.' : 'fields must be complete.'
          }`,
        });
        return override;
      }
    }),
  );
};

export const foundFeatureWithDuplicateOverride = (
  features: Feature<Geometry>[],
  setOpenDialog: Dispatch<SetStateAction<{ type: DialogType; title: string } | undefined>>,
): Feature<Geometry> | undefined => {
  return features.find(f => {
    const propertyValueSet = new Set<string>();
    const propertyDeviceMap = new Map<string, Set<string>>();
    const uniqueOverrides = f.get('geomobyOverrides')?.filter((override: GeomobyOverride) => {
      const propertyValueKey = JSON.stringify({
        property: override.property,
        value: override.value,
      });

      if (propertyValueSet.has(propertyValueKey)) return false;

      propertyValueSet.add(propertyValueKey);
      const existingIds = propertyDeviceMap.get(override.property) || new Set<string>();

      for (const id of override.deviceIds) {
        if (existingIds.has(id)) return false;
      }

      override.deviceIds.forEach(deviceId => existingIds.add(deviceId));
      propertyDeviceMap.set(override.property, existingIds);
      return true;
    });

    if (uniqueOverrides.length < f.get('geomobyOverrides')?.length) {
      setOpenDialog({
        type: DialogType.DirtySave,
        title: 'Geomoby Overrides must be unique.',
      });
      return f;
    }
    return undefined;
  });
};

export const propertyIsInvalid = (property: string, value: string, prop: Property): boolean => {
  const lowerLimit = prop.limit?.[0];
  const upperLimit = prop.limit?.[1];
  if (lowerLimit === undefined || upperLimit === undefined) {
    return prop.type !== PropertyType.List;
  }
  if (prop.label === property) {
    // Outside of number limit.
    if (
      prop.type === PropertyType.Number &&
      (Number(value) < lowerLimit || Number(value) > upperLimit)
    ) {
      return true;
    }
    // Outside of string limit.
    else if (
      prop.type === PropertyType.String &&
      (String(value).length < lowerLimit || String(value).length > upperLimit)
    ) {
      return true;
    }
    // Fails regex test.
    else if (!!prop.pattern && !new RegExp(prop.pattern).test(value)) {
      return true;
    }
  }
  return false;
};

export const foundFeatureWithInvalidPropertyOrOverride = (
  features: Feature<Geometry>[],
  properties: Property[],
  setOpenDialog: (properties: { type: DialogType; title: string } | undefined) => void,
): Feature<Geometry> | undefined => {
  const featureWithInvalidGeomobyProperty = features
    .filter(feature => feature.get('geomobyProperties'))
    .find(feature => {
      return Object.entries(feature.get('geomobyProperties')).find(([property, value]) =>
        properties.find(prop => {
          const foundInvalidProperty = propertyIsInvalid(property, value as string, prop);
          if (foundInvalidProperty) {
            setOpenDialog({
              type: DialogType.DirtySave,
              title: prop.message,
            });
          }
          return foundInvalidProperty;
        }),
      );
    });
  const featureWithInvalidGeomobyOverride = features
    .filter(feature => feature.get('geomobyOverrides'))
    .find(feature => {
      return feature.get('geomobyOverrides').find((override: GeomobyOverride) =>
        properties.find(prop => {
          const foundInvalidOverride = propertyIsInvalid(
            override.property,
            override.value as string,
            prop,
          );
          if (foundInvalidOverride) {
            setOpenDialog({
              type: DialogType.DirtySave,
              title: prop.message,
            });
          }
          return foundInvalidOverride;
        }),
      );
    });

  return featureWithInvalidGeomobyProperty ?? featureWithInvalidGeomobyOverride;
};

export const featureHasInvalidBreachBufferGeometry = (
  olmap: OlMap,
  source: VectorSource<Geometry>,
  polygon: Feature<Geometry>,
  containedFeatures: Feature<Geometry>[],
): boolean => {
  const containedBreachZone = containedFeatures
    .filter(f => f.get('zone') === FenceZone.breach)
    .find(cf => cf.get('id') === polygon.get('parentId'));
  if (polygon.get('zone') === FenceZone.buffer && !containedBreachZone) return true;
  if (polygon.get('zone') !== FenceZone.breach) return false;

  const foundBufferZone = findBufferZone(olmap, polygon.get('id'), polygon.get('layerId'));
  const geometry = foundBufferZone?.getGeometry();
  if (!foundBufferZone) return false;

  return !polygonsContainedWithinPolygon(geometry as Geometry, source)?.find(
    f => f.get('id') === foundBufferZone.get('parentId'),
  );
};

export const foundFeatureWithInvalidBreachGeometry = (
  olmap: OlMap,
  layer: VectorLayer<VectorSource<Geometry>>,
): Feature<Geometry> | undefined => {
  const source = layer.getSource();
  return source
    .getFeatures()
    .filter(feature => feature.get('zone') === FenceZone.breach)
    .find(feature => featureHasInvalidBreachBufferGeometry(olmap, source, feature, []));
};
